import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Dices, ChevronDown, Mail, Send, Target, Users, Package, ShieldCheck, Zap, Images, Bot } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DROPDOWN_W = 220;

function ModeDropdown({ label, icon, className, wrapperClassName = "", dropdownAlign = "left" }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const dropRef = useRef(null);
  const minigamesTapRef = useRef({ count: 0, lastTap: 0 });
  const navigate = useNavigate();

  // Hidden Junk Minigames entry — 5-click-within-500ms passthrough.
  const handleMinigamesTap = () => {
    const now = Date.now();
    const tapDelay = now - minigamesTapRef.current.lastTap;
    minigamesTapRef.current.count = tapDelay > 500 ? 1 : minigamesTapRef.current.count + 1;
    minigamesTapRef.current.lastTap = now;
    if (minigamesTapRef.current.count === 5) {
      setOpen(false);
      minigamesTapRef.current.count = 0;
      navigate("/minigames");
    }
  };

  const reposition = useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const dropH = 220;
    const top = r.bottom + 4 + dropH > vh ? r.top - dropH - 4 : r.bottom + 4;
    let left = dropdownAlign === "right" ? r.right - DROPDOWN_W : r.left;
    left = Math.max(8, Math.min(left, vw - DROPDOWN_W - 8));
    setPos({ top, left });
  }, [dropdownAlign]);

  useEffect(() => {
    if (!open) return;
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (dropRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const startBotGame = () => {
    setOpen(false);
    navigate("/bot-setup");
  };

  const startBotVsBot = () => {
    setOpen(false);
    navigate("/bot-vs-bot-setup");
  };

  const dropdown = open && createPortal(
    <div
      ref={dropRef}
      style={{ position: "fixed", top: pos.top, left: pos.left, width: DROPDOWN_W, zIndex: 9999 }}
      className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden"
    >
      <button
        onClick={() => { setOpen(false); navigate("/online-setup"); }}
        className="w-full px-4 py-3 text-left text-sm font-bold text-white hover:bg-slate-800 flex items-center gap-3 transition-colors"
      >
        <Zap size={15} className="text-blue-400 flex-shrink-0" />
        <div>
          <div>Head to Head (Online)</div>
          <div className="text-xs font-normal text-slate-500">Play on separate devices</div>
        </div>
      </button>
      <div className="h-px bg-slate-800" />
      <button
        onClick={startBotGame}
        className="w-full px-4 py-3 text-left text-sm font-bold text-white hover:bg-slate-800 flex items-center gap-3 transition-colors"
      >
        <Bot size={15} className="text-purple-400 flex-shrink-0" />
        <div>
          <div>vs Bot</div>
          <div className="text-xs font-normal text-slate-500">See what the bot's got</div>
        </div>
      </button>
      <div className="h-px bg-slate-800" />
      <button
        onClick={startBotVsBot}
        className="w-full px-4 py-3 text-left text-sm font-bold text-white hover:bg-slate-800 flex items-center gap-3 transition-colors"
      >
        <Bot size={15} className="text-emerald-400 flex-shrink-0" />
        <div>
          <div>Bot vs Bot</div>
          <div className="text-xs font-normal text-slate-500">Spectate the action</div>
        </div>
      </button>
      <div className="h-px bg-slate-800" />
      <button
        onClick={handleMinigamesTap}
        className="w-full px-4 py-3 text-left text-sm font-bold text-slate-600 hover:text-slate-500 flex items-center gap-3 transition-colors cursor-not-allowed opacity-50"
      >
        <Dices size={15} className="text-slate-700 flex-shrink-0" />
        <div>
          <div>Junk Minigames</div>
          <div className="text-xs font-normal text-slate-700">Coming soon</div>
        </div>
      </button>
    </div>,
    document.body
  );

  return (
    <div className={`relative ${wrapperClassName}`}>
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        className={`w-full ${className}`}
      >
        {icon}
        {label}
        <ChevronDown size={12} className={`ml-1 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {dropdown}
    </div>
  );
}

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } };

const MANAGER_CARDS = [
  "Rally Caps", "Bench Clearing", "Clean Game*", "Nasty Stuff", "Pine Tar",
  "Torpedo Bat", "Juiced Up", "The Perfect Game", "Overclocked", "Amped Up",
  "Lingering Injury", "Tough Hop", "Kick Dirt", "Infield Shift", "+1 Hot Dog",
  "Pep Talk", "Plunk Them", "Catcher in the Right", "Ball in Play",
  "Calling Your Shot", "Through the Legs", "Got Your Number", "Foul Play", "Just Checking",
];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Shuffled once at module load — randomizes on each page reload
const SHUFFLED_CARDS = shuffle(MANAGER_CARDS);

function MarqueeTrack() {
  return (
    <div className="flex items-center shrink-0" aria-hidden="true">
      {SHUFFLED_CARDS.map((name, i) => (
        <span key={i} className="inline-flex items-center whitespace-nowrap">
          <span className="px-5 text-[11px] font-bold text-slate-600 uppercase tracking-widest">{name}</span>
          <span className="text-amber-900/50 text-[7px] px-1">✦</span>
        </span>
      ))}
    </div>
  );
}

function MarqueeTicker() {
  return (
    <div className="w-full overflow-hidden mt-10 border-t border-b border-slate-800/60 py-3 relative">
      <div className="absolute inset-y-0 left-0 w-16 z-10 pointer-events-none"
        style={{ background: "linear-gradient(to right, hsl(222 47% 4%), transparent)" }} />
      <div className="absolute inset-y-0 right-0 w-16 z-10 pointer-events-none"
        style={{ background: "linear-gradient(to left, hsl(222 47% 4%), transparent)" }} />
      {/* Two identical tracks — CSS slides the pair left by exactly one track width (50%) */}
      <div className="animate-marquee flex">
        <MarqueeTrack />
        <MarqueeTrack />
      </div>
    </div>
  );
}

// HERO PHOTOS — add real shots here when ready
// { src: "/photos/shot1.jpg", caption: "Game night setup", label: "Setup" }
const SLIDES = [
  { src: "/jb-test.jpeg", caption: "Game night setup", label: "Setup" },
  { src: "/jb-test2.jpeg", caption: "Ripping packs", label: "Packs" },
  { src: "/jb-test3.jpeg", caption: "Manager Cards in play", label: "Cards" },
// { src: "/jb-test4.jpeg", caption: "The diamond", label: "Field" },
];


function Lightbox({ index, onClose }) {
  const [active, setActive] = useState(index);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="relative w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-slate-400 hover:text-white text-xs font-bold uppercase tracking-widest"
        >
          Close ✕
        </button>

        {/* Image */}
        <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-slate-900 border border-slate-700">
          {SLIDES[active].src ? (
            <img src={SLIDES[active].src} alt={SLIDES[active].caption} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-3">
              <div className="w-20 h-20 rounded-full bg-slate-800 flex items-center justify-center">
                <Dices size={36} className="text-slate-600" />
              </div>
              <p className="text-slate-500 text-sm font-bold uppercase tracking-widest">{SLIDES[active].caption}</p>
              <p className="text-slate-700 text-xs">Photo coming soon</p>
            </div>
          )}
          {/* Prev / Next */}
          <button
            onClick={() => setActive((i) => (i - 1 + SLIDES.length) % SLIDES.length)}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/80 flex items-center justify-center transition-all"
          >
            <ChevronDown size={18} className="text-white rotate-90" />
          </button>
          <button
            onClick={() => setActive((i) => (i + 1) % SLIDES.length)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/80 flex items-center justify-center transition-all"
          >
            <ChevronDown size={18} className="text-white -rotate-90" />
          </button>
        </div>

        {/* Thumbnail strip */}
        <div className="flex gap-2 mt-3">
          {SLIDES.map((slide, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`flex-1 h-14 rounded-xl overflow-hidden border transition-all ${
                active === i ? "border-amber-400" : "border-slate-800 opacity-50 hover:opacity-80"
              } bg-slate-900 flex items-center justify-center`}
            >
              {slide.src
                ? <img src={slide.src} alt={slide.label} className="w-full h-full object-cover" />
                : <span className="text-[9px] font-bold text-slate-600 uppercase">{slide.label}</span>
              }
            </button>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);


  useEffect(() => {
    const t = setInterval(() => setActiveSlide((i) => (i + 1) % SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <Lightbox index={lightboxIndex} onClose={() => setLightboxIndex(null)} />
        )}
      </AnimatePresence>

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-3 bg-slate-950/80 backdrop-blur-md border-b border-slate-800/50">
        <span className="flex items-center gap-2 font-black text-lg tracking-tight text-white">
          <img src="/favicons32x32.png" alt="" className="w-5 h-5" />
          JUNK <span className="text-amber-400">BALL</span>
        </span>
        <div className="flex items-center gap-4">
          <Link to="/print-and-play" className="text-xs text-slate-400 hover:text-white transition-colors font-medium">Print & Play</Link>
          <Link to="/contact" className="text-xs text-slate-400 hover:text-white transition-colors font-medium">Contact</Link>
          <ModeDropdown
            label="Play Now"
            dropdownAlign="right"
            className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs transition-all flex items-center gap-1"
          />
        </div>
      </nav>

      {/* Hero — fullscreen image, frosted glass panel over center */}
      <section className="relative h-screen overflow-hidden pt-14 flex flex-col">

        {/* Background slides */}
        {SLIDES.map((slide, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{ opacity: activeSlide === i ? 1 : 0 }}
          >
            {slide.src
              ? <img src={slide.src} alt={slide.caption} className="w-full h-full object-cover" />
              : <div className="w-full h-full bg-slate-900" />
            }
          </div>
        ))}

        {/* Subtle dark vignette — edges only */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/50 pointer-events-none" />

        {/* Frosted glass lockup */}
        <motion.div
          variants={fadeUp} initial="hidden" animate="show" transition={{ duration: 0.6 }}
          className="relative z-10 flex-1 flex flex-col items-center justify-center px-4"
        >
          <div className="flex flex-col items-center gap-5 text-center px-10 py-10 rounded-3xl bg-black/50 backdrop-blur-md border border-white/10 max-w-md w-full shadow-2xl">
            <motion.img
              src="/junkball-logo.png"
              alt="Junk Ball"
              initial={{ scale: 0.88, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="w-56 sm:w-72 drop-shadow-2xl pointer-events-none"
            />

            <p className="text-white/60 font-bold text-sm tracking-widest uppercase -mt-2">
              The Juiced Up Baseball Board Game
            </p>

            <p className="text-white/50 text-sm leading-relaxed">
              Junk Ball is a dice thrower and card strategy game made for ripping packs, scouring cards, building dream lineups, and letting iconic moments unfold across the field. With Junk Ball, every card in your collection becomes a playable piece with its own unique quirks and characteristics.
            </p>

            <div className="flex items-center gap-2 text-white/30 text-xs font-bold tracking-wide">
              <span>2–4 Players</span><span>·</span>
              <span>Ages 12+</span><span>·</span>
              <span>5 / 7 / 9 Innings</span>
            </div>

            <div className="flex items-center gap-3 w-full">
              <ModeDropdown
                label="Play Now"
                icon={<Dices size={16} />}
                wrapperClassName="flex-1"
                className="py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-black text-sm transition-all shadow-lg shadow-amber-500/30 flex items-center justify-center gap-2"
              />
              <button
                onClick={() => document.getElementById("what-you-need")?.scrollIntoView({ behavior: "smooth" })}
                className="flex-1 py-3 rounded-xl border border-white/20 hover:border-white/40 text-white/70 hover:text-white font-bold text-sm transition-all flex items-center justify-center gap-2"
              >
                Learn More <ChevronDown size={16} />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Bullet indicators + gallery link — pinned bottom */}
        <div className="relative z-10 flex items-center justify-center gap-3 pb-6 pt-4">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveSlide(i)}
              className={`rounded-full transition-all duration-300 ${
                activeSlide === i
                  ? "bg-amber-400 w-5 h-1.5"
                  : "bg-white/30 hover:bg-white/50 w-1.5 h-1.5"
              }`}
            />
          ))}
          <Link
            to="/gallery"
            title="View full gallery"
            className="ml-2 text-white/30 hover:text-white/80 transition-colors"
          >
            <Images size={14} />
          </Link>
        </div>

      </section>


      {/* What You Need */}
      <section id="what-you-need" className="py-20 px-4 bg-slate-900/40">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto"
        >
          <p className="text-center text-xs font-bold text-amber-400 tracking-widest uppercase mb-3">What You Need</p>
          <h2 className="text-3xl sm:text-4xl font-black text-center mb-10">Two ways to play.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              {
                label: "Core",
                accent: "border-slate-700 bg-slate-800/30",
                header: "text-slate-300",
                items: [
                  "Manager Cards",
                  "Pitch Chips x4 (x2 sets)", 
                  "Baseball cards w/ stats (x2 lineups)",
                  "D10 x2, D6 x2, D20 x2 (used by some Manager Cards)",
                  "No field needed — make your own!",
                ],
              },
              {
                label: "Enhanced",
                accent: "border-amber-500/30 bg-amber-500/5",
                header: "text-amber-400",
                items: [
                  "Junk Ball Official Board",
                  "Unopened packs",
                  "Scoresheets",
                  "Card display stands",
                  "Dry erase markers",
                  "Penny sleeves / Top loaders",
                  "Sunflower seeds, peanuts, Big League Chew",
                ],
              },
            ].map((col, i) => (
              <motion.div
                key={i}
                variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className={`rounded-2xl border p-6 ${col.accent}`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <Package size={16} className={col.header} />
                  <span className={`text-xs font-black tracking-widest uppercase ${col.header}`}>Junk Ball: {col.label}</span>
                </div>
                <ul className="space-y-2">
                  {col.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-400">
                      <span className="text-slate-600 mt-0.5">—</span> {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>


      {/* Build Your Roster */}
      <section id="build-roster" className="py-20 px-4 bg-slate-950">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto text-center"
        >
          <p className="text-xs font-bold text-amber-400 tracking-widest uppercase mb-3">Build Your Roster</p>
          <h2 className="text-3xl sm:text-4xl font-black mb-4">Draft from cards you already own.</h2>
          <p className="text-slate-400 text-sm leading-relaxed max-w-xl mx-auto mb-10">
            Or rip packs with friends, trade for your favorite players, and create never-before-seen lineups. All you need to field a team:
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {["2+ Pitchers","Catcher","1st Baseman","2nd Baseman","Shortstop","3rd Baseman","Left Fielder","Center Fielder","Right Fielder","Designated Hitter"].map((pos) => (
              <span key={pos} className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs font-bold text-slate-300">
                {pos}
              </span>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How to Play */}
      <section id="how-it-works" className="py-20 px-4 bg-slate-900/50">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          <p className="text-center text-xs font-bold text-amber-400 tracking-widest uppercase mb-3">How It Works</p>
          <h2 className="text-3xl sm:text-4xl font-black text-center mb-3">Three phases of the game.</h2>
          <p className="text-center text-slate-500 text-sm max-w-xl mx-auto mb-12">
            Junk Ball mixes dice rolls with real player histories and stat-boosting Manager Cards — turning pitcher-batter duels, base running, and defensive plays into a tug-of-war between luck and strategy.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
            {[
              {
                icon: <Target size={28} className="text-red-400" />,
                label: "01 — Pitching/Batting",
                title: "Roll D10s",
                body: <>Pitcher and batter each roll a D10 adding any pitch chip and Manager Card modifiers. Pitcher wins = strike. Batter wins = contact. Any tie is a foul.*<br /><br />* 00/00 = wild pitch & 1/1 = hit by pitch.</>,
                accent: "border-red-500/20 bg-red-500/5",
              },
              {
                icon: <Dices size={28} className="text-emerald-400" />,
                label: "02 — Fielding",
                title: "Roll D6",
                body: <>After contact, the fielding team rolls a D6. The difference between the batter's contact and the fielder's roll determines the outcome.* <br /><br />* Contact of 7+ is a Home Run.</>,
                accent: "border-emerald-500/20 bg-emerald-500/5",
              },
              {
                icon: <Users size={28} className="text-blue-400" />,
                label: "03 — Baserunning",
                title: "Outs & Bases",
                body: "A positive difference means bases. Negative means outs. Hit -2 or worse with runners on first and it's a double play. Tie goes to the batter.",
                accent: "border-blue-500/20 bg-blue-500/5",
              },
            ].map((card, i) => (
              <motion.div
                key={i}
                variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`rounded-2xl border p-6 flex flex-col gap-3 ${card.accent}`}
              >
                <div>{card.icon}</div>
                <p className="text-[10px] font-bold text-slate-500 tracking-widest uppercase">{card.label}</p>
                <h3 className="text-xl font-black">{card.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{card.body}</p>
              </motion.div>
            ))}
          </div>

        </motion.div>
      </section>

      {/* Manager Cards */}
      <section id="manager-cards" className="py-20 px-4 bg-slate-950">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto"
        >
          <p className="text-center text-xs font-bold text-amber-400 tracking-widest uppercase mb-3">Manager Cards</p>
          <h2 className="text-3xl sm:text-4xl font-black text-center mb-4">Strategy meets chaos.</h2>
          <p className="text-center text-slate-400 text-sm max-w-xl mx-auto mb-10">
            Manager Cards give players a way to influence the game outside of just dice rolls. Build your team for power, pitching, and/or speed then use Manager Cards to give you the edge in those game-defining moments. 
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: <ShieldCheck size={18} className="text-purple-400" />, title: "Fresh Ideas", body: "Explore new strategies by discarding any number of active/inactive cards at the end of each half inning and drawing back up." },
              { icon: <Target size={18} className="text-red-400" />, title: "Stat Checks", body: "Manager Cards use real stats from your players' cards triggering postive or negative modifiers." },
              { icon: <Zap size={18} className="text-amber-400" />, title: "Modifier Stacking", body: "Modifiers stack to the highest card max listed. Play one or chain them together for maximum effect." },
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="rounded-xl border border-slate-800 bg-slate-900/50 p-5 flex flex-col gap-2"
              >
                <div className="flex items-center gap-2">
                  {item.icon}
                  <span className="text-xs font-black text-slate-300 uppercase tracking-wide">{item.title}</span>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">{item.body}</p>
              </motion.div>
            ))}
          </div>

          {/* Marquee ticker */}
          <MarqueeTicker />
        </motion.div>
      </section>


      {/* Closing quote */}
      <section className="py-20 px-4 bg-slate-900/30">
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto text-center"
        >
          <p className="text-slate-400 text-base sm:text-lg leading-relaxed mb-6">
            Junk Ball blends real baseball stats, strategy, and the thrill of ripping packs into a game as unpredictable as baseball itself. Every matchup, every roll, every card in your shoebox — bent corners, missing chunks, overprints — can still be the star of the show.
          </p>
          <p className="text-2xl sm:text-3xl font-black text-amber-400 italic">
            "Every card deserves its day."
          </p>
        </motion.div>
      </section>

      {/* Newsletter */}
      <NewsletterSection />

      {/* Footer */}
      <footer className="py-10 px-4 border-t border-slate-800 text-center">
        <div className="flex items-center justify-center gap-6 text-xs text-slate-600">
          <span>© {new Date().getFullYear()} Junk Ball</span>
          <span className="text-slate-800">·</span>
          <Link to="/contact" className="hover:text-slate-400 transition-colors">Contact</Link>
          <span className="text-slate-800">·</span>
          <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="hover:text-slate-400 transition-colors">Back to top</button>
        </div>
        <p className="mt-3 text-[10px] text-slate-700 italic">Every card deserves its day.</p>
      </footer>
    </div>
  );
}

function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/subscribe`, { email: email.trim() });
      setStatus("success");
      setEmail("");
    } catch (err) {
      if (err.response?.status === 409) setStatus("duplicate");
      else setStatus("error");
    }
  };

  return (
    <section id="newsletter" className="py-24 px-4 bg-slate-950 border-t border-slate-800/50">
      <motion.div
        variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} transition={{ duration: 0.5 }}
        className="max-w-lg mx-auto text-center"
      >
        <Mail size={28} className="text-amber-400 mx-auto mb-4" />
        <h2 className="text-3xl font-black mb-2">Stay in the loop.</h2>
        <p className="text-slate-400 text-sm mb-8 leading-relaxed">
          Get updates on the newest Manager Cards and series releases. No spam, just junk!
        </p>

        {status === "success" ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="py-4 px-6 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-bold text-sm"
          >
            You're in the lineup. We'll be in touch.
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="flex-1 px-4 py-3 rounded-xl bg-slate-800/80 border border-slate-700 text-white placeholder-slate-500 text-sm outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/50 transition-all"
            />
            <button
              type="submit"
              disabled={status === "loading"}
              className="px-5 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-black text-sm transition-all flex items-center gap-2 justify-center disabled:opacity-60"
            >
              <Send size={14} />
              {status === "loading" ? "..." : "Sign Me Up"}
            </button>
          </form>
        )}

        {status === "duplicate" && (
          <p className="mt-3 text-xs text-slate-500">You're already on the list. We've got you.</p>
        )}
        {status === "error" && (
          <p className="mt-3 text-xs text-red-400">Something went wrong. Try again.</p>
        )}
      </motion.div>
    </section>
  );
}
